import {
  type EDiscountAssignmentType,
  type IDiscount,
  type IDiscountRedemption,
  type IDiscountTargetCriteria,
  type IDiscountType,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { type IDiscountAssignmentAdmin, type IDiscountState } from './types';

export const preloadedState: IDiscountState = {
  ...asyncStateInitial,
  discountTypes: undefined,
  discounts: undefined,
  redemptions: undefined,
  assignments: undefined,
};

const discountSlice = createSlice({
  name: 'Discount',
  initialState: preloadedState,
  reducers: {
    discountTypesLoad(state) {
      startLoading(state);
    },
    discountTypesLoadResponse: {
      reducer(state, action: { payload: { discountTypes: IDiscountType[] } }) {
        state.discountTypes = action.payload.discountTypes;
        finishLoading(state);
      },
      prepare(discountTypes: IDiscountType[]) {
        return { payload: { discountTypes } };
      },
    },
    discountsLoad(state) {
      startLoading(state);
    },
    discountsLoadResponse: {
      reducer(state, action: { payload: { discounts: IDiscount[] } }) {
        state.discounts = action.payload.discounts;
        finishLoading(state);
      },
      prepare(discounts: IDiscount[]) {
        return { payload: { discounts } };
      },
    },
    discountSave: {
      reducer(state) {
        startLoading(state);
      },
      prepare(payload: {
        discountType: { uuid: string };
        value: number;
        description: string;
        code?: string;
        validFrom?: Date | null;
        validTo?: Date | null;
        multiUse?: boolean;
        maxRedemptions?: number | null;
        singleUsePerCustomer?: boolean;
        combinable?: boolean;
        assignmentType?: EDiscountAssignmentType;
        targetCriteria?: IDiscountTargetCriteria | null;
        minOrderValue?: number | null;
        eligibleCountryTypeIsoCodes?: string[] | null;
        shippingFreeWeightKg?: number | null;
        referrerRewardDiscountTypeUUID?: string;
        referrerRewardValue?: number | null;
        referralMinOrderValue?: number | null;
        referralValidationWindowDays?: number | null;
        productUuid?: string;
        categoryUuid?: string;
        individualCustomerUuids?: string[];
      }) {
        return { payload };
      },
    },
    discountDelete: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    discountUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(payload: {
        uuid: string;
        value?: number;
        description?: string;
        validFrom?: Date | null;
        validTo?: Date | null;
        maxRedemptions?: number | null;
        singleUsePerCustomer?: boolean;
        combinable?: boolean;
        assignmentType?: EDiscountAssignmentType;
        targetCriteria?: IDiscountTargetCriteria | null;
        minOrderValue?: number | null;
        eligibleCountryTypeIsoCodes?: string[] | null;
        shippingFreeWeightKg?: number | null;
        referrerRewardDiscountTypeUUID?: string;
        referrerRewardValue?: number | null;
        referralMinOrderValue?: number | null;
        referralValidationWindowDays?: number | null;
        productUuid?: string;
        categoryUuid?: string;
      }) {
        return { payload };
      },
    },
    discountAssignToCustomers: {
      reducer(state) {
        startLoading(state);
      },
      prepare(discountUuids: string[], customerUuids: string[]) {
        return { payload: { discountUuids, customerUuids } };
      },
    },
    discountAssignToCustomersResponse: {
      reducer(state) {
        finishLoading(state);
      },
      prepare(
        assignments: {
          customerUuid: string;
          newlyAssignedDiscountCodes: string[];
        }[],
      ) {
        return { payload: { assignments } };
      },
    },
    discountSetEnabled: {
      reducer(state) {
        startLoading(state);
      },
      prepare(uuid: string, enabled: boolean) {
        return { payload: { uuid, enabled } };
      },
    },
    discountRedemptionsList: {
      reducer(state) {
        startLoading(state);
      },
      prepare(discountUUID: string) {
        return { payload: { discountUUID } };
      },
    },
    discountRedemptionsListResponse: {
      reducer(
        state,
        action: {
          payload: {
            redemptions: IDiscountRedemption[];
            assignments?: IDiscountAssignmentAdmin[];
          };
        },
      ) {
        state.redemptions = action.payload.redemptions;
        state.assignments = action.payload.assignments;
        finishLoading(state);
      },
      prepare(
        redemptions: IDiscountRedemption[],
        assignments?: IDiscountAssignmentAdmin[],
      ) {
        return { payload: { redemptions, assignments } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Discount'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  discountTypesLoad,
  discountTypesLoadResponse,
  discountsLoad,
  discountsLoadResponse,
  discountSave,
  discountDelete,
  discountUpdate,
  discountAssignToCustomers,
  discountAssignToCustomersResponse,
  discountSetEnabled,
  discountRedemptionsList,
  discountRedemptionsListResponse,
} = discountSlice.actions;
export default discountSlice.reducer;
